import React, { useMemo, useState } from 'react';
import { vibrar } from '../../utils/hapticos';
import { PagoPlan, Plan } from '../../types';
import { useAMedias } from '../../context/AMediasContext';
import { useAuth } from '../../context/AuthContext';
import { aCentimos, aEuros } from '../../utils/amediasUtils';
import { ArrowLeftRight } from 'lucide-react';
import {
  Campo, claseInput, aInput, deInput, leerImporte, MensajeError, ModalBase, BotonesModal,
} from './ui';

interface Props {
  plan: Plan;
  pago?: PagoPlan;
  /** Para «Saldar» desde quién debe a quién: el pago ya rellenado. */
  sugerido?: { de: string; a: string; importe: number };
  onClose: () => void;
}

/** Dinero que alguien le da a otro para saldar lo que le debe. */
export const ModalPago: React.FC<Props> = ({ plan, pago, sugerido, onClose }) => {
  const { crearPago, actualizarPago } = useAMedias();
  const { user } = useAuth();

  const participantes = useMemo(() => plan.participantes.filter(p =>
    p.activo || p.id === pago?.de || p.id === pago?.a || p.id === sugerido?.de || p.id === sugerido?.a),
  [plan, pago, sugerido]);
  const yo = plan.participantes.find(p => p.uid === user?.uid);

  const base = pago ?? sugerido;
  const [de, setDe] = useState(base?.de ?? yo?.id ?? participantes[0]?.id ?? '');
  const [a, setA] = useState(base?.a ?? participantes.find(p => p.id !== (base?.de ?? yo?.id))?.id ?? '');
  const [importe, setImporte] = useState(base ? String(base.importe).replace('.', ',') : '');
  const [fecha, setFecha] = useState(aInput(pago?.fecha ?? new Date()));
  const [nota, setNota] = useState(pago?.nota ?? '');
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);

  const nombre = (id: string) => {
    const p = participantes.find(x => x.id === id);
    return p ? (p.id === yo?.id ? `${p.nombre} (tú)` : p.nombre) : '';
  };

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    const total = aEuros(aCentimos(leerImporte(importe)));
    if (!de || !a) { setError('Indica quién paga y quién recibe.'); return; }
    if (de === a) { setError('Quien paga y quien recibe tienen que ser distintos.'); return; }
    if (total <= 0) { setError('Indica el importe.'); return; }
    if (!fecha) { setError('Indica la fecha.'); return; }

    setGuardando(true);
    setError('');
    const datos = { de, a, importe: total, fecha: deInput(fecha)!, nota: nota.trim() || undefined };
    try {
      if (pago) await actualizarPago(plan.id, pago.id, datos);
      else await crearPago(plan.id, datos);
      vibrar.exito();
      onClose();
    } catch (err) {
      console.error(err);
      vibrar.error();
      setError('No se pudo guardar. Inténtalo de nuevo.');
      setGuardando(false);
    }
  };

  return (
    <ModalBase titulo={pago ? 'Editar pago' : 'Registrar pago'} onClose={onClose}>
      <form onSubmit={enviar} className="p-5 space-y-4">
        <MensajeError texto={error} />

        <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-2">
          <Campo label="Paga">
            <select value={de} onChange={e => setDe(e.target.value)} className={claseInput}>
              {participantes.map(p => <option key={p.id} value={p.id}>{nombre(p.id)}</option>)}
            </select>
          </Campo>
          <button type="button" onClick={() => { setDe(a); setA(de); vibrar.suave(); }}
            aria-label="Intercambiar quién paga y quién recibe" title="Intercambiar"
            className="mb-1 p-2 rounded-full text-gray-500 hover:text-gray-800 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-700">
            <ArrowLeftRight className="w-5 h-5" />
          </button>
          <Campo label="Recibe">
            <select value={a} onChange={e => setA(e.target.value)} className={claseInput}>
              {participantes.map(p => <option key={p.id} value={p.id}>{nombre(p.id)}</option>)}
            </select>
          </Campo>
        </div>

        <div className="grid gap-3 grid-cols-2">
          <Campo label="Importe (€) *">
            <input type="text" inputMode="decimal" value={importe} onChange={e => setImporte(e.target.value)}
              placeholder="0,00" className={claseInput} autoFocus={!sugerido} />
          </Campo>
          <Campo label="Fecha *">
            <input type="date" value={fecha} onChange={e => setFecha(e.target.value)} className={claseInput} />
          </Campo>
        </div>

        <Campo label="Nota">
          <input type="text" value={nota} onChange={e => setNota(e.target.value)}
            placeholder="Bizum, efectivo…" className={claseInput} />
        </Campo>

        <BotonesModal guardando={guardando} texto={pago ? 'Guardar cambios' : 'Registrar pago'} onClose={onClose} />
      </form>
    </ModalBase>
  );
};
